import React from 'react';
import { PageSection } from '../../../types/pageBuilder';

interface SectionControlsProps {
  section: PageSection;
  onEdit: () => void;
  onToggleVisibility: () => void;
  onDelete: () => void;
  isEditable: boolean;
}

/**
 * Section control buttons component
 * Visibility toggle, edit, and delete buttons
 */
const SectionControls: React.FC<SectionControlsProps> = ({
  section,
  onEdit,
  onToggleVisibility,
  onDelete,
  isEditable
}) => {
  return (
    <div className="flex items-center space-x-2">
      {/* Visibility Toggle */}
      <button
        onClick={onToggleVisibility}
        className={`p-2 rounded transition-colors ${
          section.is_visible
            ? 'bg-green-50 text-green-600 hover:bg-green-100'
            : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
        }`}
        title={section.is_visible ? 'Hide section' : 'Show section'}
      >
        {section.is_visible ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
            />
          </svg>
        )}
      </button>

      {/* Edit Button */}
      <button
        onClick={onEdit}
        disabled={!isEditable}
        className={`p-2 rounded transition-colors ${
          isEditable
            ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            : 'bg-gray-50 text-gray-300 cursor-not-allowed'
        }`}
        title={isEditable ? 'Edit section' : 'Section not editable (dynamic content)'}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </button>

      {/* Delete Button */}
      <button
        onClick={onDelete}
        className="p-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
        title="Delete section"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>

      {/* Drag Handle */}
      <div className="cursor-move p-2 text-gray-400 hover:text-gray-600" title="Drag to reorder">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 8h16M4 16h16"
          />
        </svg>
      </div>
    </div>
  );
};

export default SectionControls;
